import {
    CheckCircleOutlined,
    DownloadOutlined,
    EditOutlined,
    EyeOutlined,
    LockOutlined,
    SearchOutlined,
    StarOutlined,
    TeamOutlined,
    TrophyOutlined,
    UnlockOutlined,
    UserOutlined,
    MinusCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Input,
    InputNumber,
    Layout,
    message,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import dayjs from 'dayjs';
import {
    collection,
    doc,
    getDocs,
    query,
    serverTimestamp,
    updateDoc,
    where,
    writeBatch,
    deleteField,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { competitionList } from '../../constant/RegisterPageConst';
import { useAuth } from '../../context/DataContext';
import { db } from '../../firebase';
import { calculateAward } from '../../utils/Utils';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

/**
 * Get the effective score for average calculation.
 * Uses adminAdjustedScore if present, otherwise falls back to score.
 */
const getEffectiveScore = (scoreDoc) => {
    if (scoreDoc.adminAdjustedScore !== undefined && scoreDoc.adminAdjustedScore !== null) {
        return scoreDoc.adminAdjustedScore;
    }
    return scoreDoc.score;
};

/**
 * Calculate average from an array of score documents.
 */
const calcAverage = (scoreDocs) => {
    const valid = scoreDocs.filter(
        (s) => typeof getEffectiveScore(s) === 'number'
    );
    if (valid.length === 0) return null;
    const sum = valid.reduce((acc, s) => acc + getEffectiveScore(s), 0);
    return Math.round((sum / valid.length) * 100) / 100;
};

/**
 * Get performer display name from registrant data.
 */
const getPerformerName = (reg) => {
    const p = reg.performers?.[0];
    if (!p) return 'Unknown';
    return p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown';
};

/**
 * Color code for scores.
 */
const getScoreColor = (score) => {
    if (score === null || score === undefined) return '#999';
    if (score >= 90) return '#52c41a';
    if (score >= 75) return '#faad14';
    if (score >= 60) return '#ff7a45';
    return '#ff4d4f';
};

/**
 * ScoringRecap — Admin page to review, adjust, and finalize all jury scores.
 */
const ScoringRecap = () => {
    const { loggedInUser } = useAuth();

    // State
    const [selectedEvent, setSelectedEvent] = useState('APCS2025');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [registrants, setRegistrants] = useState([]);
    const [allScores, setAllScores] = useState([]); // flat array of all JuryScores2025 docs
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    // Editing state
    const [editingKey, setEditingKey] = useState(null); // scoreDocId being edited
    const [editingValue, setEditingValue] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);

    // Finalization state
    const [finalizingId, setFinalizingId] = useState(null);

    // Category options
    const categoryOptions = Object.values(competitionList).map((cat) => ({
        label: cat,
        value: cat,
    }));

    // Fetch data when category or event changes
    useEffect(() => {
        if (selectedCategory && selectedEvent) {
            fetchData(selectedCategory, selectedEvent);
        }
    }, [selectedCategory, selectedEvent]);

    const fetchData = async (category, eventId) => {
        setLoading(true);
        try {
            // Fetch registrants for this category
            const regQuery = query(
                collection(db, 'Registrants2025'),
                where('eventId', '==', eventId),
                where('competitionCategory', '==', category)
            );
            const regSnap = await getDocs(regQuery);
            const regs = regSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

            // Fetch ALL jury scores for this category (not filtered by juryUserId)
            const scoresQuery = query(
                collection(db, 'JuryScores2025'),
                where('competitionCategory', '==', category)
            );
            const scoresSnap = await getDocs(scoresQuery);
            const scores = scoresSnap.docs.map((d) => ({
                docId: d.id,
                ...d.data(),
                timestamp: d.data().timestamp?.toDate?.() || null,
            }));

            setRegistrants(regs);
            setAllScores(scores);
        } catch (error) {
            console.error('Error fetching scoring data:', error);
            message.error('Failed to load scoring data');
        } finally {
            setLoading(false);
        }
    };

    // Group scores by registrantId
    const scoresByRegistrant = useMemo(() => {
        const map = {};
        allScores.forEach((s) => {
            if (!map[s.registrantId]) map[s.registrantId] = [];
            map[s.registrantId].push(s);
        });
        return map;
    }, [allScores]);

    // Build table data
    const tableData = useMemo(() => {
        return registrants.map((reg) => {
            const scores = scoresByRegistrant[reg.id] || [];
            let avg = calcAverage(scores);
            const isFinalized = scores.length > 0 && scores.every((s) => s.isFinalized === true);
            const isSomeFinalized = scores.some((s) => s.isFinalized === true);

            // Apply penalty if it exists
            if (avg !== null && reg.penaltyScore) {
                avg = Math.round((avg - reg.penaltyScore) * 100) / 100;
            }

            let status;
            if (isFinalized) status = 'Finalized';
            else if (scores.length === 0) status = 'Not Scored';
            else status = 'Scored';

            return {
                key: reg.id,
                registrant: reg,
                name: getPerformerName(reg),
                ageCategory: reg.ageCategory || '—',
                performanceCategory: reg.PerformanceCategory || 'Solo',
                scores,
                originalAverage: calcAverage(scores),
                penaltyScore: reg.penaltyScore || 0,
                penaltyReason: reg.penaltyReason || '',
                average: avg,
                status,
                isFinalized,
                isSomeFinalized,
                juryCount: scores.length,
            };
        });
    }, [registrants, scoresByRegistrant]);

    // Filter
    const filteredData = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return tableData.filter((row) => {
            if (statusFilter !== 'All') {
                if (statusFilter === 'Scored' && row.status !== 'Scored') return false;
                if (statusFilter === 'Not Scored' && row.status !== 'Not Scored') return false;
                if (statusFilter === 'Finalized' && row.status !== 'Finalized') return false;
            }
            if (q && !row.name.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [tableData, searchText, statusFilter]);

    // Stats
    const stats = useMemo(() => ({
        total: tableData.length,
        scored: tableData.filter((r) => r.status === 'Scored').length,
        notScored: tableData.filter((r) => r.status === 'Not Scored').length,
        finalized: tableData.filter((r) => r.status === 'Finalized').length,
    }), [tableData]);

    // --- Admin edit score ---
    const handleStartEdit = (scoreDocId, currentAdminScore, originalScore) => {
        setEditingKey(scoreDocId);
        setEditingValue(currentAdminScore ?? originalScore);
    };

    const handleSaveEdit = async (scoreDocId) => {
        if (editingValue === null || editingValue === undefined) {
            message.warning('Please enter a score');
            return;
        }
        if (editingValue < 0 || editingValue > 100) {
            message.error('Score must be between 0 and 100');
            return;
        }
        setSavingEdit(true);
        try {
            const docRef = doc(db, 'JuryScores2025', scoreDocId);
            await updateDoc(docRef, {
                adminAdjustedScore: Number(editingValue),
                adminAdjustedAt: serverTimestamp(),
                adminAdjustedBy: loggedInUser?.email || 'admin',
            });
            // Update local state
            setAllScores((prev) =>
                prev.map((s) =>
                    s.docId === scoreDocId
                        ? {
                            ...s,
                            adminAdjustedScore: Number(editingValue),
                            adminAdjustedAt: new Date(),
                            adminAdjustedBy: loggedInUser?.email || 'admin',
                        }
                        : s
                )
            );
            setEditingKey(null);
            setEditingValue(null);
            message.success('Score adjusted successfully');
        } catch (error) {
            console.error('Error saving admin score:', error);
            message.error('Failed to save score adjustment');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleRemoveAdjustment = async (scoreDocId) => {
        setSavingEdit(true);
        try {
            const docRef = doc(db, 'JuryScores2025', scoreDocId);
            await updateDoc(docRef, {
                adminAdjustedScore: deleteField(),
                adminAdjustedAt: deleteField(),
                adminAdjustedBy: deleteField(),
            });
            // Update local state
            setAllScores((prev) =>
                prev.map((s) => {
                    if (s.docId === scoreDocId) {
                        const newS = { ...s };
                        delete newS.adminAdjustedScore;
                        delete newS.adminAdjustedAt;
                        delete newS.adminAdjustedBy;
                        return newS;
                    }
                    return s;
                })
            );
            setEditingKey(null);
            setEditingValue(null);
            message.success('Admin adjustment removed');
        } catch (error) {
            console.error('Error removing admin score:', error);
            message.error('Failed to remove score adjustment');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingKey(null);
        setEditingValue(null);
    };

    // --- Penalty ---
    const [penalizingId, setPenalizingId] = useState(null);

    const handleApplyPenalty = async (registrantId) => {
        setPenalizingId(registrantId);
        try {
            const docRef = doc(db, 'Registrants2025', registrantId);
            await updateDoc(docRef, {
                penaltyScore: 10,
                penaltyReason: 'Reading a book during performance',
                penaltyAppliedAt: serverTimestamp(),
                penaltyAppliedBy: loggedInUser?.email || 'admin'
            });
            setRegistrants(prev => prev.map(r => r.id === registrantId ? {
                ...r,
                penaltyScore: 10,
                penaltyReason: 'Reading a book during performance'
            } : r));
            message.success('Penalty of -10 applied');
        } catch (error) {
            console.error('Error applying penalty:', error);
            message.error('Failed to apply penalty');
        } finally {
            setPenalizingId(null);
        }
    };

    const handleRemovePenalty = async (registrantId) => {
        setPenalizingId(registrantId);
        try {
            const docRef = doc(db, 'Registrants2025', registrantId);
            await updateDoc(docRef, {
                penaltyScore: deleteField(),
                penaltyReason: deleteField(),
                penaltyAppliedAt: deleteField(),
                penaltyAppliedBy: deleteField()
            });
            setRegistrants(prev => prev.map(r => {
                if (r.id === registrantId) {
                    const newR = { ...r };
                    delete newR.penaltyScore;
                    delete newR.penaltyReason;
                    return newR;
                }
                return r;
            }));
            message.success('Penalty removed');
        } catch (error) {
            console.error('Error removing penalty:', error);
            message.error('Failed to remove penalty');
        } finally {
            setPenalizingId(null);
        }
    };

    // --- Finalize / Unfinalize ---
    const handleToggleFinalize = async (registrantId, shouldFinalize) => {
        const scoreDocs = scoresByRegistrant[registrantId] || [];
        if (scoreDocs.length === 0) {
            message.warning('No scores to finalize for this registrant');
            return;
        }
        setFinalizingId(registrantId);
        try {
            const batch = writeBatch(db);
            scoreDocs.forEach((s) => {
                const docRef = doc(db, 'JuryScores2025', s.docId);
                batch.update(docRef, {
                    isFinalized: shouldFinalize,
                    ...(shouldFinalize
                        ? {
                            finalizedAt: serverTimestamp(),
                            finalizedBy: loggedInUser?.email || 'admin',
                        }
                        : {
                            finalizedAt: null,
                            finalizedBy: null,
                        }),
                });
            });
            await batch.commit();

            // Update local state
            setAllScores((prev) =>
                prev.map((s) =>
                    s.registrantId === registrantId
                        ? {
                            ...s,
                            isFinalized: shouldFinalize,
                            finalizedAt: shouldFinalize ? new Date() : null,
                            finalizedBy: shouldFinalize ? (loggedInUser?.email || 'admin') : null,
                        }
                        : s
                )
            );
            message.success(
                shouldFinalize
                    ? 'Score finalized — jury can no longer edit'
                    : 'Score unfinalized — jury can now edit again'
            );
        } catch (error) {
            console.error('Error finalizing scores:', error);
            message.error('Failed to update finalization status');
        } finally {
            setFinalizingId(null);
        }
    };

    // --- CSV Export ---
    const handleExportCSV = () => {
        if (filteredData.length === 0) {
            message.warning('No data to export');
            return;
        }

        // Build CSV rows
        const headers = [
            'Participant Name',
            'Category',
            'Age Category',
            'Performance Type',
            'Jury Count',
            'Average Score',
            'Status',
        ];

        // Find max jury count for dynamic columns
        const maxJuries = Math.max(...filteredData.map((r) => r.juryCount), 0);
        for (let i = 1; i <= maxJuries; i++) {
            headers.push(`Jury ${i} Name`);
            headers.push(`Jury ${i} Original Score`);
            headers.push(`Jury ${i} Adjusted Score`);
            headers.push(`Jury ${i} Feedback`);
            headers.push(`Jury ${i} APCS Notes`);
        }

        const rows = filteredData.map((row) => {
            const base = [
                `"${row.name}"`,
                selectedCategory,
                row.ageCategory,
                row.performanceCategory,
                row.juryCount,
                row.average !== null ? row.average : '',
                row.status,
            ];

            for (let i = 0; i < maxJuries; i++) {
                const s = row.scores[i];
                if (s) {
                    base.push(`"${s.juryName || 'Unknown'}"`);
                    base.push(s.score ?? '');
                    base.push(s.adminAdjustedScore ?? '');
                    base.push(`"${(s.comment || '').replace(/"/g, '""')}"`);
                    base.push(`"${(s.panelComment || '').replace(/"/g, '""')}"`);
                } else {
                    base.push('', '', '', '', '');
                }
            }
            return base.join(',');
        });

        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `scoring_recap_${selectedCategory}_${dayjs().format('YYYY-MM-DD')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        message.success('CSV exported successfully');
    };

    // --- Expanded row renderer ---
    const expandedRowRender = (record) => {
        if (record.scores.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No jury scores submitted yet"
                    style={{ padding: '16px 0' }}
                />
            );
        }

        return (
            <div style={{ padding: '8px 0' }}>
                <Row gutter={[16, 16]}>
                    {record.scores.map((s) => {
                        const effectiveScore = getEffectiveScore(s);
                        const isEditing = editingKey === s.docId;
                        const hasAdminAdjustment = s.adminAdjustedScore !== undefined && s.adminAdjustedScore !== null;

                        return (
                            <Col xs={24} lg={12} key={s.docId}>
                                <Card
                                    size="small"
                                    title={
                                        <Space>
                                            <UserOutlined />
                                            <Text strong>{s.juryName || 'Unknown Jury'}</Text>
                                            {record.isFinalized && (
                                                <Tag icon={<LockOutlined />} color="green">
                                                    Finalized
                                                </Tag>
                                            )}
                                        </Space>
                                    }
                                    extra={
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {s.timestamp
                                                ? dayjs(s.timestamp).format('MMM D, YYYY h:mm A')
                                                : 'No date'}
                                        </Text>
                                    }
                                    style={{
                                        borderLeft: `4px solid ${getScoreColor(effectiveScore)}`,
                                    }}
                                >
                                    {/* Score section */}
                                    <Descriptions column={1} size="small" bordered={false}>
                                        <Descriptions.Item label="Original Score">
                                            <Text
                                                strong
                                                style={{
                                                    fontSize: 18,
                                                    color: getScoreColor(s.score),
                                                }}
                                            >
                                                {s.score}/100
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Admin Adjusted Score">
                                            {isEditing ? (
                                                <Space>
                                                    <InputNumber
                                                        min={0}
                                                        max={100}
                                                        value={editingValue}
                                                        onChange={setEditingValue}
                                                        style={{ width: 80 }}
                                                        autoFocus
                                                    />
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        onClick={() => handleSaveEdit(s.docId)}
                                                        loading={savingEdit}
                                                    >
                                                        Save
                                                    </Button>
                                                    {hasAdminAdjustment && (
                                                        <Popconfirm
                                                            title="Remove adjustment and revert to jury score?"
                                                            onConfirm={() => handleRemoveAdjustment(s.docId)}
                                                            okText="Yes"
                                                            cancelText="No"
                                                        >
                                                            <Button size="small" danger loading={savingEdit}>
                                                                Remove
                                                            </Button>
                                                        </Popconfirm>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        onClick={handleCancelEdit}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Space>
                                            ) : (
                                                <Space>
                                                    {hasAdminAdjustment ? (
                                                        <Text
                                                            strong
                                                            style={{
                                                                fontSize: 18,
                                                                color: getScoreColor(s.adminAdjustedScore),
                                                            }}
                                                        >
                                                            {s.adminAdjustedScore}/100
                                                        </Text>
                                                    ) : (
                                                        <Text type="secondary">Not adjusted</Text>
                                                    )}
                                                    {!record.isFinalized && (
                                                        <Tooltip title="Adjust this jury's score (jury won't see this change)">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EditOutlined />}
                                                                onClick={() =>
                                                                    handleStartEdit(
                                                                        s.docId,
                                                                        s.adminAdjustedScore,
                                                                        s.score
                                                                    )
                                                                }
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {hasAdminAdjustment && (
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: 11 }}
                                                        >
                                                            by {s.adminAdjustedBy}
                                                        </Text>
                                                    )}
                                                </Space>
                                            )}
                                        </Descriptions.Item>
                                    </Descriptions>

                                    {/* Performance feedback */}
                                    {s.comment && (
                                        <div style={{ marginTop: 12 }}>
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
                                            >
                                                Performance Feedback (for performer):
                                            </Text>
                                            <Paragraph
                                                style={{
                                                    background: '#f5f5f5',
                                                    padding: 12,
                                                    borderRadius: 6,
                                                    borderLeft: '3px solid #EBBC64',
                                                    marginBottom: 0,
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {s.comment}
                                            </Paragraph>
                                        </div>
                                    )}

                                    {/* APCS team notes */}
                                    {s.panelComment && (
                                        <div style={{ marginTop: 12 }}>
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
                                            >
                                                Notes (for APCS team):
                                            </Text>
                                            <Paragraph
                                                style={{
                                                    background: '#f0f5ff',
                                                    padding: 12,
                                                    borderRadius: 6,
                                                    borderLeft: '3px solid #1890ff',
                                                    marginBottom: 0,
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {s.panelComment}
                                            </Paragraph>
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        );
                    })}
                </Row>

                {/* Average summary in expanded */}
                <Card
                    size="small"
                    style={{ marginTop: 12, background: '#fafafa' }}
                >
                    <Row gutter={24} align="middle">
                        <Col>
                            <Statistic
                                title="Average Score (effective)"
                                value={record.average !== null ? record.average : '—'}
                                suffix={record.average !== null ? '/ 100' : ''}
                                valueStyle={{
                                    color: getScoreColor(record.average),
                                    fontSize: 24,
                                }}
                            />
                        </Col>
                        {record.penaltyScore > 0 && (
                            <Col>
                                <Statistic
                                    title="Penalty Applied"
                                    value={`-${record.penaltyScore}`}
                                    valueStyle={{ color: '#ff4d4f', fontSize: 18 }}
                                    prefix={<WarningOutlined />}
                                />
                                <div style={{ fontSize: 11, color: '#888', marginTop: -4 }}>{record.penaltyReason}</div>
                            </Col>
                        )}
                        <Col>
                            <Statistic
                                title="Jury Members"
                                value={record.juryCount}
                                prefix={<TeamOutlined />}
                            />
                        </Col>
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                            <Space wrap>
                                {!record.isFinalized && (
                                    record.penaltyScore > 0 ? (
                                        <Popconfirm
                                            title="Remove penalty?"
                                            onConfirm={() => handleRemovePenalty(record.key)}
                                        >
                                            <Button danger loading={penalizingId === record.key}>
                                                Remove Penalty
                                            </Button>
                                        </Popconfirm>
                                    ) : (
                                        <Popconfirm
                                            title="Apply -10 penalty for reading a book?"
                                            onConfirm={() => handleApplyPenalty(record.key)}
                                        >
                                            <Button danger icon={<MinusCircleOutlined />} loading={penalizingId === record.key}>
                                                Penalize (-10)
                                            </Button>
                                        </Popconfirm>
                                    )
                                )}
                                {record.isFinalized ? (
                                    <Popconfirm
                                        title="Unfinalize this registrant?"
                                        description="Jury members will be able to edit their scores again."
                                        onConfirm={() => handleToggleFinalize(record.key, false)}
                                        okText="Yes, unfinalize"
                                    >
                                        <Button
                                            icon={<UnlockOutlined />}
                                            loading={finalizingId === record.key}
                                        >
                                            Unfinalize
                                        </Button>
                                    </Popconfirm>
                                ) : (
                                    <Popconfirm
                                        title="Finalize this registrant's scores?"
                                        description="Jury members will no longer be able to edit their scores for this registrant."
                                        onConfirm={() => handleToggleFinalize(record.key, true)}
                                        okText="Yes, finalize"
                                        okButtonProps={{ type: 'primary' }}
                                        disabled={record.juryCount === 0}
                                    >
                                        <Button
                                            type="primary"
                                            icon={<LockOutlined />}
                                            loading={finalizingId === record.key}
                                            disabled={record.juryCount === 0}
                                        >
                                            Mark as Finalized
                                        </Button>
                                    </Popconfirm>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </div>
        );
    };

    // --- Table columns ---
    const columns = [
        {
            title: 'Participant',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Space>
                    <div
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: '#EBBC64',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: 12,
                        }}
                    >
                        {name
                            .split(' ')
                            .map((w) => w[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                    </div>
                    <div>
                        <Text strong>{name}</Text>
                        <br />
                        <Tag
                            color={
                                record.performanceCategory === 'Ensemble'
                                    ? 'purple'
                                    : 'blue'
                            }
                            style={{ fontSize: 10 }}
                        >
                            {record.performanceCategory === 'Ensemble'
                                ? `Ensemble · ${record.registrant.performers?.length || 0} members`
                                : 'Individual'}
                        </Tag>
                    </div>
                </Space>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Age Category',
            dataIndex: 'ageCategory',
            key: 'ageCategory',
            width: 150,
            render: (val) => <Text>{val}</Text>,
        },
        {
            title: 'Jury Scores',
            key: 'juryScores',
            width: 280,
            render: (_, record) => {
                if (record.scores.length === 0) {
                    return <Text type="secondary">No scores</Text>;
                }
                return (
                    <Space wrap size={[4, 4]}>
                        {record.scores.map((s) => {
                            const effective = getEffectiveScore(s);
                            const hasAdj =
                                s.adminAdjustedScore !== undefined &&
                                s.adminAdjustedScore !== null;
                            return (
                                <Tooltip
                                    key={s.docId}
                                    title={
                                        hasAdj
                                            ? `${s.juryName}: Original ${s.score}, Adjusted ${s.adminAdjustedScore}`
                                            : `${s.juryName}: ${s.score}/100`
                                    }
                                >
                                    <Tag
                                        color={getScoreColor(effective) === '#52c41a' ? 'success'
                                            : getScoreColor(effective) === '#faad14' ? 'warning'
                                                : getScoreColor(effective) === '#ff7a45' ? 'orange'
                                                    : 'error'}
                                        style={{ fontSize: 11 }}
                                    >
                                        {(s.juryName || 'Jury').split(' ').slice(-1)[0]}:{' '}
                                        <strong>{effective}</strong>
                                        {hasAdj && ' *'}
                                    </Tag>
                                </Tooltip>
                            );
                        })}
                    </Space>
                );
            },
        },
        {
            title: 'Average',
            dataIndex: 'average',
            key: 'average',
            width: 100,
            sorter: (a, b) => (a.average ?? -1) - (b.average ?? -1),
            defaultSortOrder: 'descend',
            render: (avg) =>
                avg !== null ? (
                    <Text
                        strong
                        style={{
                            fontSize: 18,
                            color: getScoreColor(avg),
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {avg}
                    </Text>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: 'Award',
            key: 'award',
            width: 120,
            align: 'center',
            render: (_, record) => {
                const award = record.average !== null ? calculateAward(record.average) : 'N/A';
                let color = 'default';
                const awardLower = String(award).toLowerCase();
                if (awardLower.includes('sapphire')) color = 'purple';
                else if (awardLower.includes('diamond')) color = 'cyan';
                else if (awardLower.includes('gold')) color = 'gold';
                else if (awardLower.includes('silver')) color = 'geekblue';
                else if (awardLower.includes('bronze')) color = 'orange';

                if (award === 'N/A') return <Tag>-</Tag>;

                return (
                    <Space direction="vertical" size={2} style={{ display: 'flex', alignItems: 'center' }}>
                        <Tag color={color} style={{ fontWeight: 700, borderRadius: '4px', textTransform: 'uppercase', margin: 0 }}>
                            {award}
                        </Tag>
                        {record.isFinalized && (
                            <div style={{ fontSize: '10px', color: '#52c41a', display: 'flex', alignItems: 'center', gap: '2px', marginTop: '2px' }}>
                                <CheckCircleOutlined style={{ fontSize: '10px' }} /> VERIFIED
                            </div>
                        )}
                    </Space>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            filters: [
                { text: 'Not Scored', value: 'Not Scored' },
                { text: 'Scored', value: 'Scored' },
                { text: 'Finalized', value: 'Finalized' },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status) => {
                if (status === 'Finalized') {
                    return (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                            Finalized ✅
                        </Tag>
                    );
                }
                if (status === 'Scored') {
                    return <Tag color="processing">Scored</Tag>;
                }
                return <Tag color="default">Not Scored</Tag>;
            },
        },
        {
            title: 'Juries',
            dataIndex: 'juryCount',
            key: 'juryCount',
            width: 70,
            align: 'center',
            render: (count) => (
                <Badge
                    count={count}
                    showZero
                    style={{ backgroundColor: count > 0 ? '#52c41a' : '#d9d9d9' }}
                />
            ),
        },
    ];

    return (
        <Content style={{ padding: '24px 50px', minHeight: 280 }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 20,
                }}
            >
                <div>
                    <Title level={2} style={{ marginBottom: 4 }}>
                        <TrophyOutlined style={{ marginRight: 10 }} />
                        Scoring Recap
                    </Title>
                    <Text type="secondary">
                        Review jury scores, adjust if needed, and finalize results.
                        Finalized scores are locked and jury members can no longer edit them.
                    </Text>
                </div>
                <Space>
                    <Select
                        value={selectedEvent}
                        onChange={(val) => {
                            setSelectedEvent(val);
                            setSearchText('');
                            setStatusFilter('All');
                        }}
                        options={[
                            { label: 'APCS 2025', value: 'APCS2025' },
                            { label: 'APCS 2026', value: 'APCS2026' },
                        ]}
                        style={{ width: 140 }}
                        size="large"
                    />
                    <Select
                        placeholder="Select competition category"
                        value={selectedCategory}
                        onChange={(val) => {
                            setSelectedCategory(val);
                            setSearchText('');
                            setStatusFilter('All');
                        }}
                        options={categoryOptions}
                        style={{ width: 220 }}
                        showSearch
                        size="large"
                    />
                </Space>
            </div>

            {!selectedCategory ? (
                <Card style={{ textAlign: 'center', padding: 60 }}>
                    <Empty
                        image={<TrophyOutlined style={{ fontSize: 64, color: '#EBBC64' }} />}
                        description={
                            <span style={{ fontSize: 16 }}>
                                Select a competition category above to view scoring recap
                            </span>
                        }
                    />
                </Card>
            ) : loading ? (
                <div style={{ textAlign: 'center', padding: 80 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Loading scores for {selectedCategory}...</div>
                </div>
            ) : (
                <>
                    {/* Stats row */}
                    <Row gutter={16} style={{ marginBottom: 20 }}>
                        <Col xs={24} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Total Registrants"
                                    value={stats.total}
                                    prefix={<UserOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Scored"
                                    value={stats.scored}
                                    prefix={<StarOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Not Scored"
                                    value={stats.notScored}
                                    valueStyle={{
                                        color: stats.notScored > 0 ? '#ff4d4f' : '#52c41a',
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Finalized"
                                    value={stats.finalized}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Info alert */}
                    <Alert
                        message="How admin score adjustments work"
                        description="When you adjust a jury's score, the change is stored separately — the jury member still sees their original score. The average is calculated using the adjusted score when available."
                        type="info"
                        showIcon
                        closable
                        style={{ marginBottom: 16 }}
                    />

                    {/* Filter bar */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Row gutter={12} align="middle">
                            <Col flex="auto">
                                <Input
                                    placeholder="Search by participant name..."
                                    prefix={<SearchOutlined />}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    allowClear
                                />
                            </Col>
                            <Col>
                                <Select
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    style={{ width: 150 }}
                                    options={[
                                        { label: 'All Statuses', value: 'All' },
                                        { label: 'Scored', value: 'Scored' },
                                        { label: 'Not Scored', value: 'Not Scored' },
                                        { label: 'Finalized', value: 'Finalized' },
                                    ]}
                                />
                            </Col>
                            <Col>
                                <Button
                                    icon={<DownloadOutlined />}
                                    onClick={handleExportCSV}
                                    disabled={filteredData.length === 0}
                                >
                                    Export CSV
                                </Button>
                            </Col>
                        </Row>
                    </Card>

                    {/* Main table */}
                    <Card bodyStyle={{ padding: 0 }}>
                        <Table
                            columns={columns}
                            dataSource={filteredData}
                            expandable={{
                                expandedRowRender,
                                expandRowByClick: true,
                                expandIcon: ({ expanded, onExpand, record }) => (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={(e) => onExpand(record, e)}
                                        style={{
                                            color: expanded ? '#1890ff' : undefined,
                                        }}
                                    >
                                        {expanded ? 'Hide' : 'Review'}
                                    </Button>
                                ),
                            }}
                            pagination={{
                                pageSize: 15,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '15', '25', '50'],
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} registrants`,
                            }}
                            rowClassName={(record) =>
                                record.isFinalized ? 'scoring-recap-finalized-row' : ''
                            }
                            size="middle"
                            scroll={{ x: 900 }}
                            locale={{
                                emptyText: (
                                    <Empty
                                        description={`No registrants found for ${selectedCategory}`}
                                    />
                                ),
                            }}
                        />
                    </Card>

                    {/* Legend */}
                    <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                        <Space split="·">
                            <span>* = admin adjusted score</span>
                            <span>Average uses adjusted score when available</span>
                            <span>Finalized scores are locked from jury editing</span>
                        </Space>
                    </div>
                </>
            )}

            {/* Minimal inline styles for finalized row */}
            <style>{`
                .scoring-recap-finalized-row {
                    background: #f6ffed !important;
                }
                .scoring-recap-finalized-row td {
                    opacity: 0.85;
                }
            `}</style>
        </Content>
    );
};

export default ScoringRecap;
